// "Continue with Google" button. Uses the Lovable-managed OAuth broker so it
// works inside the editor iframe, previews, and the published site without
// custom credentials. The broker returns tokens to `redirect_uri`, so we
// point it at the public `/auth/callback` route which finishes the flow.
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { lovable } from "@/integrations/lovable";
import { friendlyAuthError } from "@/lib/auth-validation";

interface Props {
  /** Optional same-origin redirect to preserve through OAuth. */
  redirect?: string;
  label?: string;
  onError?: (message: string) => void;
}

export function GoogleSignInButton({ redirect, label = "Continue with Google", onError }: Props) {
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const callback = new URL("/auth/callback", origin);
      if (redirect && redirect.startsWith("/")) {
        callback.searchParams.set("redirect", redirect);
      }
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: callback.toString(),
      });
      if (result.error) {
        console.error("[auth] google sign-in error:", result.error);
        onError?.(friendlyAuthError(result.error));
        return;
      }
      // If `result.redirected` the browser is navigating away; otherwise the
      // preview iframe flow set the session in-place and we route now.
      if (!result.redirected) {
        window.location.href = callback.toString();
      }
    } catch (err) {
      console.error("[auth] google sign-in failed:", err);
      onError?.(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-input bg-background px-4 py-2.5 text-sm font-semibold text-foreground shadow-sm transition hover:bg-accent disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <GoogleGlyph className="h-4 w-4" />
      )}
      {label}
    </button>
  );
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2C41.9 35.7 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
